import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query'
import { API_COMMON_CACHE_TIME, HELPER_OPTION_MAX_ROWS } from '@/utils/consts'
import { SelectOptionType } from '@/components/content/SelectBoxTypes'
import { getShipperListV2 } from '@/services/shipper/apis'
import {
  confirmStockTransfer,
  deleteErpDocuments,
  getErpDocumentItems,
  getErpDocumentSummaries,
  getGoodsCodeOptions,
  getRemoveLogs,
  getStyleNumberOptions,
  saveErpDocuments,
} from './apis'
import {
  ConfirmStockTransferReq,
  DeleteErpDocumentsReq,
  DeleteErpDocumentsRes,
  DocumentRemoveLog,
  ErpDocumentItem,
  ErpDocumentSummary,
  GetErpDocumentsReq,
  GetGoodsCodeOptionsReq,
  GetStyleNumberOptionsReq,
  GoodsCodeOption,
  SaveErpDocumentsReq,
  SaveErpDocumentsRes,
  StyleNumberOption,
} from './types'

/**
 * useQuery 결과에서 `data` 만 도메인 이름으로 갈아끼운 형태 — 훅 반환 타입 명시(CLAUDE.md) 를 위해 공통화했다.
 * 훅이 `data ?? []` 로 undefined 를 흡수하므로 이름 바뀐 필드는 항상 배열이다(isFetching·refetch 등 나머지는 그대로).
 */
type NamedListQueryResult<K extends string, T> = Omit<UseQueryResult<T[], Error>, 'data'> & { [P in K]: T[] }

const PATH = ['inventory', 'stockTransferConfirm'] as const

/** EST01 계열 삭제이력 카테고리 — 1.0 removeSto 가 DocumentRemoveLog.CATEGORY='STO' 로 기록한다. */
export const REMOVE_LOG_CATEGORY = 'STO'

export const stockTransferConfirmQueryKeys = {
  all: PATH,
  summaries: (params: GetErpDocumentsReq) => [...PATH, 'SUMMARIES', params] as const,
  items: (params: GetErpDocumentsReq) => [...PATH, 'ITEMS', params] as const,
  removeLogs: [...PATH, 'REMOVE_LOGS'] as const,
  shippers: [...PATH, 'SHIPPERS'] as const,
  styleNumbers: (params: GetStyleNumberOptionsReq) => [...PATH, 'STYLE_NUMBERS', params] as const,
  goodsCodes: (params: GetGoodsCodeOptionsReq) => [...PATH, 'GOODS_CODES', params] as const,
}

/**
 * HEAD(문서) 목록.
 *
 * 진입 즉시 조회하지 않는다(`enabled: false`) — 조회 버튼으로만 실행한다(ST10/ST11 과 동일 패턴).
 * 실패(HTTP 4xx)는 reject 로 오므로 호출부가 refetch 반환값의 error 에서 message 를 꺼내 알럿한다.
 */
export const useGetErpDocumentSummaries = (params: GetErpDocumentsReq): NamedListQueryResult<'summaries', ErpDocumentSummary> => {
  const { data, ...rest } = useQuery({
    queryKey: stockTransferConfirmQueryKeys.summaries(params),
    queryFn: async () => {
      const res = await getErpDocumentSummaries(params)
      return res.data?.items ?? []
    },
    enabled: false,
  })

  return { summaries: data ?? [], ...rest }
}

/**
 * ITEM(품목) 목록 — HEAD 선택 문서 기준.
 *
 * 데모는 조회 1회분을 클라이언트에서 ORD_NO 로 거르지만, 실 데이터는 ITEM 이 무필터에 가까울수록
 * 폭증한다(prod 800만 행 — BE 무필터 400 가드) — 1.0(EST01.js: HEAD 더블클릭 시 ORD_NO 조회)과 같이
 * 선택 문서의 ordNo 로 서버 조회한다. 문서별로 캐시되어 행 이동 시 재조회가 없다.
 */
export const useGetErpDocumentItems = (params: GetErpDocumentsReq): NamedListQueryResult<'items', ErpDocumentItem> => {
  const enabled = Boolean(params.ordNo?.length)
  const { data, ...rest } = useQuery({
    queryKey: stockTransferConfirmQueryKeys.items(params),
    queryFn: async () => {
      const res = await getErpDocumentItems(params)
      return res.data?.items ?? []
    },
    enabled,
  })

  return { items: data ?? [], ...rest }
}

/** 저장 — HEAD/ITEM 편집분 일괄 반영. 조회조건은 리셋하지 않는다(MOMS 공통, 핸드오프 §화면 공통). 저장 뒤 재조회는 호출부가 한다. */
export const useSaveErpDocuments = (): UseMutationResult<ApiResponseV2<SaveErpDocumentsRes>, Error, SaveErpDocumentsReq> =>
  useMutation({ mutationFn: saveErpDocuments })

/** 확정 — 성공 시 meta.message('확정되었습니다.')와 확정 문서번호 목록이 온다. */
export const useConfirmStockTransfer = (): UseMutationResult<ApiResponseV2<{ items: string[] }>, Error, ConfirmStockTransferReq> =>
  useMutation({ mutationFn: confirmStockTransfer })

/** 삭제(auth0 전용) — 응답의 deleted/skipped 문서번호로 결과를 안내한다. */
export const useDeleteErpDocuments = (): UseMutationResult<ApiResponseV2<DeleteErpDocumentsRes>, Error, DeleteErpDocumentsReq> =>
  useMutation({ mutationFn: deleteErpDocuments })

/** 삭제이력(auth0 전용) — 모달이 열릴 때만 조회한다. 서버가 최근 30일 + 인증 사용자 화주로 스코핑한다. */
export const useGetRemoveLogs = (enabled: boolean): NamedListQueryResult<'logs', DocumentRemoveLog> => {
  const { data, ...rest } = useQuery({
    queryKey: stockTransferConfirmQueryKeys.removeLogs,
    queryFn: async () => {
      const res = await getRemoveLogs(REMOVE_LOG_CATEGORY)
      return res.data?.items ?? []
    },
    enabled,
    // 이력은 열 때마다 최신을 본다 — 삭제 직후 다시 열면 방금 삭제분이 보여야 한다.
    gcTime: 0,
  })

  return { logs: data ?? [], ...rest }
}

/**
 * 화주명 셀렉트 옵션 — `POST /api/v1/get-shippers` 전체 목록.
 *
 * WEB_STO_SUMMARY.SPR_NM 은 화주 식별 문자열이라 value=화주코드, label=화주명으로 만든다(ST10/ST11 선례).
 * 확정/삭제/저장의 shipper(shipperCode)도 선택 행의 sprNm 을 그대로 쓴다 — 셀렉트 값과 같은 식별 공간이다.
 */
export const useGetShipperOptions = (): NamedListQueryResult<'options', SelectOptionType<string>> => {
  const { data, ...rest } = useQuery({
    queryKey: stockTransferConfirmQueryKeys.shippers,
    queryFn: async (): Promise<SelectOptionType<string>[]> => {
      // 클라이언트 검색(showSearch) 전제로 전건을 한 번에 받는다(get-shipper-names 의 size=10000 선례).
      const res = await getShipperListV2({ shipperNames: [], logisticsServiceTypes: [], page: 0, size: 10000 })
      return (res?.data?.items ?? []).map((s) => ({ value: s.shipperCode, label: s.shipperName || s.shipperCode }))
    },
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { options: data ?? [], ...rest }
}

interface StyleNumberOptionsData {
  items: StyleNumberOption[]
  /** 응답이 BE 상한(1,000행)에 닿았다 — 화면이 "조건을 좁혀 달라" 안내를 띄운다. */
  isTruncated: boolean
}

/**
 * 스타일넘버 서치헬퍼 목록 — 모달이 열려 있을 때만 조회한다.
 * 2.0 응답(styleNumber·brandName·itemCode)을 모달 표 컬럼 키(STL_NO·BRD_NM·GDS_CD)로 바꿔 돌려준다. 잘림 판정은 거르기 전 건수로 한다.
 */
export const useGetStyleNumberOptions = (
  params: GetStyleNumberOptionsReq,
  enabled = true,
): Omit<UseQueryResult<StyleNumberOptionsData, Error>, 'data'> & StyleNumberOptionsData => {
  const { data, ...rest } = useQuery({
    queryKey: stockTransferConfirmQueryKeys.styleNumbers(params),
    queryFn: async (): Promise<StyleNumberOptionsData> => {
      const rows = (await getStyleNumberOptions(params)).data?.items ?? []
      return {
        items: rows
          .filter((r) => r.styleNumber) // 스타일번호가 없는 상품도 distinct 1행으로 온다 — 골라도 조건이 비므로 뺀다(브랜드 헬퍼의 NULL 제외와 같다)
          .map((r) => ({ STL_NO: r.styleNumber ?? '', BRD_NM: r.brandName ?? '', GDS_CD: r.itemCode ?? '' })),
        isTruncated: rows.length >= HELPER_OPTION_MAX_ROWS,
      }
    },
    enabled,
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { items: data?.items ?? [], isTruncated: data?.isTruncated ?? false, ...rest }
}

/** 상품코드 서치헬퍼 목록 — 모달이 열려 있을 때만 조회한다. */
export const useGetGoodsCodeOptions = (params: GetGoodsCodeOptionsReq, enabled = true): NamedListQueryResult<'items', GoodsCodeOption> => {
  const { data, ...rest } = useQuery({
    queryKey: stockTransferConfirmQueryKeys.goodsCodes(params),
    queryFn: async () => {
      const res = await getGoodsCodeOptions(params)
      return res.data?.items ?? []
    },
    enabled,
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { items: data ?? [], ...rest }
}
